<!--
	@name app-firm-webhooks
	@description Firm Webhookls
	@date 2024/07/16
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<div :component="$options.name">
		<v-sheet class="pa-6">
			<v-fade-transition hide-on-leave>
				<div v-if="loading">
					<p class="text-body-1 mb-3 text-center">
						Loading webhooks
					</p>
					<v-progress-linear height="6" indeterminate />
				</div>
			</v-fade-transition>
			<v-fade-transition hide-on-leave>
				<div v-if="!loading">
					<validation-observer v-slot="{ invalid, validate }">
						<form @submit.prevent="validate().then(submit)">
							<v-card v-for="(webhook, index) in webhookList" :key="index" :value="webhook.id" class="mt-0 mb-6">
								<v-toolbar dense flat :dark="selectedWebhooks.includes(webhook.id)" :color="selectedWebhooks.includes(webhook.id) ? 'success' : 'grey lighten-2'">
									<v-toolbar-title class="text-body-2">
										{{ webhook.name }}
									</v-toolbar-title>
									<v-spacer />
									<v-switch v-model="selectedWebhooks" :value="webhook.id" hide-details color="white" />
								</v-toolbar>
								<v-row no-gutters :style="`opacity: ${!selectedWebhooks.includes(webhook.id) ? '0.5' : '1'};`">
									<v-col class="grow">
										<v-card-text class="py-6">
											<!-- <p class="mb-5 grey--text text--darken-1 d-none">
												Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
												Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
											</p> -->
											<validation-provider
												:key="webhook.id"
												v-slot="{ errors }"
												:vid="webhook.id"
												:name="`${webhook.name} URL`"
												:rules="{ required: selectedWebhooks.includes(webhook.id) }"
											>
												<v-text-field
													v-model="formData[webhook.id]"
													:disabled="!selectedWebhooks.includes(webhook.id)"
													dense
													:label="`${webhook.name} URL`"
													outlined
													:placeholder="`${webhook.name} URL`"
													:error-messages="errors"
													hide-details=""
													:prefix="HTTP_PROTOCOL"
												/>
											</validation-provider>
											<template v-if="webhookTriggerConfig[webhook.nameUnique]">
												<p class="text-body-2 mt-4 mb-2 grey--text text--darken-2">
													Triggers <span class="error--text">*</span>
												</p>
												<div class="d-flex flex-wrap ma-n1">
													<v-chip
														v-for="trigger in webhookTriggerConfig[webhook.nameUnique]"
														:key="trigger.value"
														:color="isTriggerSelected(webhook.nameUnique, trigger.value) ? 'success' : 'grey lighten-2'"
														:dark="isTriggerSelected(webhook.nameUnique, trigger.value)"
														class="ma-1"
														@click="toggleTrigger(webhook.nameUnique, trigger.value)"
													>
														<v-icon left small>
															{{ isTriggerSelected(webhook.nameUnique, trigger.value) ? 'mdi-check-circle' : 'mdi-circle-outline' }}
														</v-icon>
														{{ trigger.label }}
													</v-chip>
												</div>
												<p v-if="triggerErrors[webhook.nameUnique] && selectedWebhooks.includes(webhook.id)" class="error--text text-caption mt-2 mb-0">
													{{ triggerErrors[webhook.nameUnique] }}
												</p>
											</template>
										</v-card-text>
									</v-col>
								</v-row>
							</v-card>
							<v-btn v-if="selectedWebhooks.length || !existingWebhooks" color="primary" class="mx-auto d-block" :disabled="invalid || isFormInvalid || hasTriggerErrors" :loading="submitting" type="submit">
								Update webhook settings
							</v-btn>
							<v-btn v-else-if="existingWebhooks" color="error" class="mx-auto d-block" :disabled="invalid" :loading="submitting" type="submit">
								delete all webhook settings
							</v-btn>
						</form>
					</validation-observer>
				</div>
			</v-fade-transition>
		</v-sheet>
	</div>
</template>

<script>
	import { mapState, mapActions } from 'vuex';
	import { ElementTools } from '@/utils';
	import { ValidationObserver, ValidationProvider } from 'vee-validate';

	const HTTP_PROTOCOL = 'https://';

	export default {
		name: 'app-firm-webhooks',

		components: { ValidationProvider, ValidationObserver },

		props: {},

		data() {
			return {
				loading: false,
				submitting: false,
				formData: {},
				existingWebhooks: false,
				webhookList: [],
				selectedWebhooks: [],
				HTTP_PROTOCOL,
				selectedTriggers: {},
				triggerErrors: {},
				submitAttempted: false,
				webhookTriggerConfig: {
					results_pdf_document: [
						{ value: 'kfiRequested', label: 'KFI Requested' },
						{ value: 'kfiReceived', label: 'KFI Received' },
						{ value: 'suitabilityReportProduced', label: 'Suitability Report Produced' }
					],
				},
				submitAttempted: false
			};
		},

		computed: {
			...mapState('Account', ['organisation']),
			...mapState('CmsSite', ['site']),

			hasTriggerErrors() {
				return this.submitAttempted && Object.entries(this.webhookTriggerConfig).some(([ nameUnique ]) => {
					const webhook = this.webhookList.find((w) => w.nameUnique === nameUnique);
					return webhook && this.selectedWebhooks.includes(webhook.id) && !this.selectedTriggers[nameUnique]?.length;
				});
			},

			isFormInvalid() {
				return !this.selectedWebhooks.length && !this.existingWebhooks;
			}
		},

		watch: {
			selectedWebhooks(val) {
				Object.keys(this.webhookTriggerConfig).forEach((nameUnique) => {
					const webhook = this.webhookList.find((w) => w.nameUnique === nameUnique);
					if (webhook && !val.includes(webhook.id)) {
						this.$set(this.selectedTriggers, nameUnique, []);
						this.$set(this.triggerErrors, nameUnique, null);
						this.submitAttempted = false;
					}
				});
			}
		},
		
		created() {
			this.setFormData();
		},

		methods: {
			...mapActions('Option', ['getWebhookList']),
			...mapActions('AccountOrganisationWebhook', ['loadOrganisationWebhooks', 'addOrganisationWebhooks']),

			async setFormData() {
				this.loading = true;
				const requests = [this.getWebhookList(), this.loadOrganisationWebhooks(this.organisation.id)];
				Promise.all(requests).then((responses) => {
					this.webhookList = responses[0].data;
					this.webhookList.forEach((webhook) => this.$set(this.formData, webhook.id, null));
					this.selectedWebhooks = responses[1].data.map((i) => i.webhookId);
					if (this.selectedWebhooks.length) this.existingWebhooks = true;
					responses[1].data.forEach((i) => {
						const webhook = responses[0].data.find((w) => w.id === i.webhookId);
						if (webhook && this.webhookTriggerConfig[webhook.nameUnique] && i.options?.triggers) {
							this.$set(this.selectedTriggers, webhook.nameUnique, i.options.triggers);
						}
						this.$set(this.formData, i.webhookId, i.url.replace(HTTP_PROTOCOL, ''));
					});
				}).catch(() => {
					ElementTools.fireNotification(this.$el, 'error', `An error occured`);
				}).finally(() => {
					this.loading = false;
				});
			},

			/**
			 * @name submit
			 * @description Triggered on submit to create or update organisation webhooks
			 * @param {Object} payload
			 */
			async submit() {
				this.submitAttempted = true;

				for (const [nameUnique] of Object.entries(this.webhookTriggerConfig)) {
					const webhook = this.webhookList.find((w) => w.nameUnique === nameUnique);
					if (webhook && this.selectedWebhooks.includes(webhook.id) && !this.selectedTriggers[nameUnique]?.length) {
						this.$set(this.triggerErrors, nameUnique, 'At least one trigger must be selected');
                        ElementTools.fireNotification(this.$el, 'error', 'Failed to updated firm webhooks. At least one trigger must be selected.');
						return;
					}
				}

				this.submitting = true;

				try {
					let payload = [];
					for (let [id, url] of Object.entries(this.formData)) {
						url = `${HTTP_PROTOCOL}${url}`;
						if (this.selectedWebhooks.includes(id)) {
							const entry = { webhookId: id, url };
							const webhook = this.webhookList.find((w) => w.id === id);
							if (webhook && this.webhookTriggerConfig[webhook.nameUnique] && this.selectedTriggers[webhook.nameUnique]?.length) {
								entry.options = { triggers: this.selectedTriggers[webhook.nameUnique] };
							}
							payload.push(entry);
						}
					}
					await this.addOrganisationWebhooks({ id: this.organisation.id, payload });
					this.existingWebhooks = this.selectedWebhooks.length;
					//this.formData.map((x, index) => !this.selectedWebhooks.includes(index) ? null : x);
					for (const [key, value] of Object.entries(this.formData)) {
						this.$set(this.formData, key, !this.selectedWebhooks.includes(key) ? null : value);
					}
					ElementTools.fireNotification(this.$el, 'success', `Your firm Webhooks have been successfully updated`);
				} catch (error) {
					ElementTools.fireNotification(this.$el, 'error', 'Failed to updated firm webhooks');
				}

				this.submitting = false;
			},

			isTriggerSelected(nameUnique, value) {
				return this.selectedTriggers[nameUnique]?.includes(value) ?? false;
			},

			toggleTrigger(nameUnique, value) {
				const current = this.selectedTriggers[nameUnique] ?? [];
				const index = current.indexOf(value);
				const updated = [...current];
				if (index === -1) updated.push(value);
				else updated.splice(index, 1);
				this.$set(this.selectedTriggers, nameUnique, updated);

				if (this.submitAttempted && !updated.length) {
					this.$set(this.triggerErrors, nameUnique, 'At least one trigger must be selected');
				} else {
					this.$set(this.triggerErrors, nameUnique, null);
				}
			}
		}
	};
</script>

<style lang="scss" scoped>
	.v-slide-group {
		::v-deep .v-slide-group__content {
			justify-content: center;
		}
	}

	::v-deep .v-input--selection-controls__input {
		margin-right: 0;
	}
</style>